import type { EnemyDef, EnemyId } from "../types";

export const ENEMIES: Record<EnemyId, EnemyDef> = {
  grunt: {
    id: "grunt",
    label: "Goblin",
    hp: 30,
    speed: 85,
    gold: 8,
    damage: 1,
    color: 0x6fae3a,
    radius: 17,
  },
  fast: {
    id: "fast",
    label: "Fast",
    hp: 18,
    speed: 150,
    gold: 10,
    damage: 1,
    color: 0xff9b3a,
    radius: 14,
  },
  tank: {
    id: "tank",
    label: "Tank",
    hp: 140,
    speed: 85,
    gold: 25,
    damage: 2,
    color: 0x7a7a8a,
    radius: 21,
  },
  boss: {
    id: "boss",
    label: "Boss",
    hp: 800,
    speed: 95,
    gold: 120,
    damage: 5,
    color: 0xc04ad8,
    radius: 27,
  },
  elite: {
    id: "elite",
    label: "Elite",
    hp: 100,
    speed: 135,
    gold: 22,
    damage: 2,
    color: 0x5599ff,
    radius: 18,
  },
  titan: {
    id: "titan",
    label: "Titan",
    hp: 600,
    speed: 70,
    gold: 90,
    damage: 4,
    color: 0x88aa66,
    radius: 30,
  },
  wraith: {
    id: "wraith",
    label: "Banshee",
    hp: 45,
    speed: 130,
    gold: 14,
    damage: 1,
    color: 0xeae8f0,
    radius: 19,
  },
  hellhound: {
    id: "hellhound",
    label: "Hellhound",
    hp: 70,
    speed: 175,
    gold: 16,
    damage: 1,
    color: 0xff4a18,
    radius: 18,
  },
  golem: {
    id: "golem",
    label: "Golem",
    hp: 320,
    speed: 65,
    gold: 55,
    damage: 3,
    color: 0x8a7a5a,
    radius: 28,
  },
  brood: {
    id: "brood",
    label: "Brood",
    hp: 95,
    speed: 100,
    gold: 20,
    damage: 2,
    color: 0xc8d040,
    radius: 22,
  },
  // ---- Wave-themed specialty creeps ----
  skeleton: {
    id: "skeleton",
    label: "Skeleton",
    hp: 22,
    speed: 110,
    gold: 9,
    damage: 1,
    color: 0xeaeaea,
    radius: 15,
  },
  demon: {
    id: "demon",
    label: "Demon",
    hp: 180,
    speed: 90,
    gold: 30,
    damage: 2,
    color: 0xff2a2a,
    radius: 22,
  },
  spider: {
    id: "spider",
    label: "Spider",
    hp: 55,
    speed: 165,
    gold: 13,
    damage: 1,
    color: 0x3a1a6a,
    radius: 16,
  },
  crystal: {
    id: "crystal",
    label: "Crystal",
    hp: 260,
    speed: 62,
    gold: 48,
    damage: 3,
    color: 0x60e0ff,
    radius: 26,
  },
  vampire: {
    id: "vampire",
    label: "Vampire",
    hp: 85,
    speed: 140,
    gold: 18,
    damage: 2,
    color: 0x8a0030,
    radius: 19,
  },
  magma_knight: {
    id: "magma_knight",
    label: "Magma Knight",
    hp: 420,
    speed: 58,
    gold: 70,
    damage: 3,
    color: 0xff6a00,
    radius: 28,
  },
  shadow: {
    id: "shadow",
    label: "Shadow",
    hp: 40,
    speed: 190,
    gold: 12,
    damage: 1,
    color: 0x1a0a3a,
    radius: 14,
  },
  // ---- Mega-bosses (solo encounters at waves 6 / 12 / 18) ----
  overlord: {
    id: "overlord",
    label: "Overlord",
    hp: 2600,
    speed: 45,
    gold: 380,
    damage: 8,
    color: 0xff4a4a,
    radius: 54,
  },
  warlord: {
    id: "warlord",
    label: "Warlord",
    hp: 5400,
    speed: 42,
    gold: 650,
    damage: 12,
    color: 0xb84aff,
    radius: 60,
  },
  worldender: {
    id: "worldender",
    label: "World Ender",
    hp: 11000,
    speed: 38,
    gold: 1100,
    damage: 16,
    color: 0xc8d040,
    radius: 65,
  },
};

/** Scale enemy HP by wave index — adjusts difficulty without rewriting wave defs. */
export function scaleHp(base: number, waveIndex: number): number {
  return Math.round(base * (1 + waveIndex * 0.15));
}

/**
 * Scale enemy speed by wave index. Grunts (wave-1 trash) and big monsters
 * (tank/boss/titan) accelerate noticeably across the run; fast/elite scale
 * only slightly so they don't outrun projectile travel times.
 */
export function scaleSpeed(base: number, id: EnemyId, waveIndex: number): number {
  const w = Math.max(1, waveIndex) - 1; // wave-1 = +0%
  let perWave = 0.01;
  let cap = 0.15;
  if (id === "grunt") {
    perWave = 0.06;
    cap = 0.6;
  } else if (id === "tank" || id === "boss" || id === "titan" || id === "golem") {
    perWave = 0.05;
    cap = 0.5;
  } else if (id === "overlord" || id === "warlord" || id === "worldender") {
    // Mega-bosses keep a slow imposing march — small speed scaling only.
    perWave = 0.02;
    cap = 0.2;
  }
  const mul = 1 + Math.min(cap, w * perWave);
  return base * mul;
}
